import {
  arrayUnion,
  deleteDoc,
  doc,
  getDoc,
  runTransaction,
  setDoc,
  Timestamp,
} from 'firebase/firestore';
import {httpsCallable} from 'firebase/functions';
import {
  DeclinedUser,
  FieldTranslation,
  FirestoreCollection,
  hasUserRespondedEventEvaluation,
  QuestionResponses,
  User,
} from 'hubbl-shared';
import {EventDTO} from '../models/event/EventDTO';
import {firestore, firebaseFunctions} from './firebase';

import {
  collection,
  getCountFromServer,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from 'firebase/firestore';

import {DocumentSnapshot} from 'firebase/firestore';
import {mapUserToUserDTO} from './utils/map-incoming-users/mapUserToUserDTO';

const addEvent = async (companyId: string, eventDto: EventDTO) => {
  const promise = new Promise<void>(async (resolve, reject) => {
    try {
      const companyRef = doc(
        firestore,
        FirestoreCollection.Companies,
        companyId,
      );

      const eventsRef = doc(
        companyRef,
        FirestoreCollection.Events,
        eventDto.id,
      );

      await setDoc(eventsRef, eventDto);

      resolve();
    } catch (error) {
      console.log('🚀🚀🚀🚀 ~ promise ~ error:', error);
      reject(error);
    }
  });
  return promise;
};

export const getEvents = async (
  companyId: string,
  limitCount: number,
  lastVisible: DocumentSnapshot<EventDTO> | null,
): Promise<{
  events: EventDTO[];
  lastVisibleSnapshot: DocumentSnapshot<EventDTO> | null;
}> => {
  try {
    const companyRef = collection(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.Events,
    );

    // Start the query after the last visible document if it's provided (for pagination)
    const eventsQuery = lastVisible
      ? query(
          companyRef,
          orderBy('created', 'desc'),
          limit(limitCount),
          startAfter(lastVisible),
        )
      : query(companyRef, orderBy('created', 'desc'), limit(limitCount));

    const querySnapshot = await getDocs(eventsQuery);

    // Map the querySnapshot to Event data
    const events: EventDTO[] = querySnapshot.docs.map(doc => {
      const data = doc.data() as EventDTO;
      return {
        ...data,
        id: doc.id,
      };
    });

    // Get the last visible document to continue pagination
    const lastVisibleSnapshot =
      querySnapshot.docs[querySnapshot.docs.length - 1] || null;

    return {
      events,

      lastVisibleSnapshot: lastVisibleSnapshot
        ? (lastVisibleSnapshot as DocumentSnapshot<EventDTO>)
        : null,
    };
  } catch (error) {
    console.error('Error fetching events:', error);
    throw new Error('Error fetching events');
  }
};
// Get a Single Event by ID
export const getEventById = async (
  companyId: string,
  eventId: string,
): Promise<EventDTO | undefined> => {
  try {
    // Reference to the event document
    const eventRef = doc(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.Events,
      eventId,
    );

    // Get the document snapshot
    const eventDoc = await getDoc(eventRef);

    // Check if the document exists
    if (eventDoc.exists()) {
      const data = eventDoc.data() as EventDTO;
      return {
        ...data,
        id: eventDoc.id,
      };
    } else {
      console.log('No such event!');
      return undefined; // Event doesn't exist
    }
  } catch (error) {
    console.error('Error fetching event:', error);
    throw new Error('Error fetching event');
  }
};

// Get future events from today 00:01 onwards, ordered by startDateTime ascending
export const getFutureEvents = async (
  companyId: string,
): Promise<{
  events: EventDTO[];
}> => {
  try {
    const companyRef = collection(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.Events,
    );

    // Create today at 00:01
    const today = new Date();
    today.setHours(0, 1, 0, 0);

    // Start the query after the last visible document if it's provided (for pagination)
    const futureEventsQuery = query(
      companyRef,
      where('startDateTime', '>=', today),
      orderBy('startDateTime', 'asc'),
    );

    const querySnapshot = await getDocs(futureEventsQuery);

    // Map the querySnapshot to Event data
    const events: EventDTO[] = querySnapshot.docs.map(doc => {
      const data = doc.data() as EventDTO;
      return {
        ...data,
        id: doc.id,
      };
    });

    return {
      events,
    };
  } catch (error) {
    console.error('Error fetching future events:', error);
    throw new Error('Error fetching future events');
  }
};

// Get past events from yesterday 23:59 backwards, ordered by startDateTime descending
export const getPastEvents = async (
  companyId: string,
  limitCount: number,
  lastVisible: DocumentSnapshot<EventDTO> | null,
): Promise<{
  events: EventDTO[];
  lastVisibleSnapshot: DocumentSnapshot<EventDTO> | null;
}> => {
  try {
    const companyRef = collection(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.Events,
    );

    // Create yesterday at 23:59
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    yesterday.setHours(23, 59, 0, 0);

    // Start the query after the last visible document if it's provided (for pagination)
    const pastEventsQuery = lastVisible
      ? query(
          companyRef,
          where('startDateTime', '<=', yesterday),
          orderBy('startDateTime', 'desc'),
          limit(limitCount),
          startAfter(lastVisible),
        )
      : query(
          companyRef,
          where('startDateTime', '<=', yesterday),
          orderBy('startDateTime', 'desc'),
          limit(limitCount),
        );

    const querySnapshot = await getDocs(pastEventsQuery);

    // Map the querySnapshot to Event data
    const events: EventDTO[] = querySnapshot.docs.map(doc => {
      const data = doc.data() as EventDTO;
      return {
        ...data,
        id: doc.id,
      };
    });

    // Get the last visible document to continue pagination
    const lastVisibleSnapshot =
      querySnapshot.docs[querySnapshot.docs.length - 1] || null;

    return {
      events,
      lastVisibleSnapshot: lastVisibleSnapshot
        ? (lastVisibleSnapshot as DocumentSnapshot<EventDTO>)
        : null,
    };
  } catch (error) {
    console.error('Error fetching past events:', error);
    throw new Error('Error fetching past events');
  }
};

const joinEvent = async (
  companyId: string,
  eventId: string,
  userAcceptor: User,
) => {
  try {
    const eventRef = doc(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.Events,
      eventId,
    );

    await runTransaction(firestore, async transaction => {
      const eventDoc = await transaction.get(eventRef);
      if (!eventDoc.exists()) {
        throw new Error('Event not found');
      }

      const eventData = eventDoc.data() as EventDTO;

      // Check if user is already in attendees to prevent duplicates
      if (
        eventData.attendees.some(attendee => attendee.id === userAcceptor.id)
      ) {
        //User is already attending this event
        return;
      }

      // Add the new user to attendees within the transaction
      transaction.update(eventRef, {
        attendees: [...eventData.attendees, mapUserToUserDTO(userAcceptor)],
        attendeeIds: arrayUnion(userAcceptor.id),
      });
    });
  } catch (error) {
    console.error('Error in joinEvent transaction:', error);
    throw new Error('Error joining event');
  }
};

const leaveEvent = async (
  companyId: string,
  eventId: string,
  userLeaving: User,
) => {
  try {
    const eventRef = doc(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.Events,
      eventId,
    );

    await runTransaction(firestore, async transaction => {
      const eventDoc = await transaction.get(eventRef);
      if (!eventDoc.exists()) {
        throw new Error('Event not found');
      }

      const eventData = eventDoc.data() as EventDTO;
      // Filter out the leaving user
      const updatedAttendees = eventData.attendees.filter(
        attendee => attendee.id !== userLeaving.id,
      );

      const currentResponses = eventData.questionResponses || {};

      // Remove the withdrawn user's responses from questionResponses

      Object.keys(currentResponses).forEach(questionId => {
        currentResponses[questionId] = currentResponses[questionId].filter(
          (response: any) => response.user.id !== userLeaving.id,
        );

        // If no responses remain for the question, delete the key
        if (currentResponses[questionId].length === 0) {
          delete currentResponses[questionId];
        }
      });

      // Update the event with the new attendees array within the transaction
      transaction.update(eventRef, {
        attendees: updatedAttendees,
        attendeeIds: updatedAttendees.map(attendee => attendee.id),
        questionResponses: currentResponses,
      });
    });
  } catch (error) {
    console.error('Error in leaveEvent transaction:', error);
    throw new Error('Error leaving event');
  }
};

const declineEvent = async (
  companyId: string,
  eventId: string,
  declinedUser: DeclinedUser,
) => {
  try {
    const eventRef = doc(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.Events,
      eventId,
    );

    await runTransaction(firestore, async transaction => {
      const eventDoc = await transaction.get(eventRef);
      console.log('HEJ EMIL');
      if (!eventDoc.exists()) {
        throw new Error('Event not found');
      }

      const eventData = eventDoc.data() as EventDTO;

      // Check if user is already in attendees to prevent duplicates
      if (
        eventData.declinedUserIds.some(
          declinedUserId => declinedUserId === declinedUser.user.id,
        )
      ) {
        //User has already declined this event
        return;
      }

      // Add the new user to declinedUserIds within the transaction
      transaction.update(eventRef, {
        declinedUserIds: arrayUnion(declinedUser.user.id),
        declinedUsers: arrayUnion(declinedUser),
      });
    });
  } catch (error) {
    console.error('Error in declineEvent transaction:', error);
    throw new Error('Error declining event');
  }
};
const acceptEventWithQuestionResponses = async (
  companyId: string,
  eventId: string,
  userAcceptor: User,
  responses: QuestionResponses,
) => {
  try {
    const eventRef = doc(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.Events,
      eventId,
    );
    await runTransaction(firestore, async transaction => {
      const eventDoc = await transaction.get(eventRef);
      if (!eventDoc.exists) {
        throw new Error('Event not found');
      }

      const remoteEvent = eventDoc.data() as EventDTO;
      const currentResponses = remoteEvent.questionResponses || {};
      const updatedResponses = {...currentResponses};

      Object.entries(responses).forEach(([questionId, response]) => {
        if (updatedResponses[questionId]) {
          updatedResponses[questionId] = [
            ...updatedResponses[questionId],
            ...response,
          ];
        } else {
          updatedResponses[questionId] = response;
        }
      });

      const updates: Record<string, any> = {
        questionResponses: updatedResponses,
      };

      // Add the new user to the attendees list
      updates.attendees = arrayUnion(mapUserToUserDTO(userAcceptor));

      updates.attendeeIds = arrayUnion(userAcceptor.id);

      transaction.update(eventRef, updates);
    });
  } catch (error) {
    console.error('Transaction failed:', error);
    throw error;
  }
};

const undeclineEvent = async (
  companyId: string,
  eventId: string,
  userId: string,
) => {
  try {
    const eventRef = doc(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.Events,
      eventId,
    );

    await runTransaction(firestore, async transaction => {
      const eventDoc = await transaction.get(eventRef);
      if (!eventDoc.exists()) {
        throw new Error('Event not found');
      }

      const eventData = eventDoc.data() as EventDTO;

      // Remove user from declined lists
      transaction.update(eventRef, {
        declinedUserIds: eventData.declinedUserIds.filter(id => id !== userId),
        declinedUsers: eventData.declinedUsers.filter(
          (user: DeclinedUser) => user.user.id !== userId,
        ),
      });
    });
  } catch (error) {
    console.error('Error in undeclineEvent transaction:', error);
    throw new Error('Error undececlining event');
  }
};

// Get events with active evaluations for a specific user
export const getEventsForEvaluation = async (
  companyId: string,
  userId: string,
): Promise<{
  events: EventDTO[];
}> => {
  try {
    const companyRef = collection(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.Events,
    );

    const now = new Date();
    const nowTimestamp = Timestamp.fromDate(now);

    // Query for events where:
    // - evaluation.endDate > now (evaluation is still active)
    // - endDateTime < now (event has passed)
    // - user is in attendeeIds array
    const evaluationEventsQuery = query(
      companyRef,
      where('evaluation.endDate', '>', nowTimestamp),
      where('endDateTime', '<', nowTimestamp),
      where('attendeeIds', 'array-contains', userId),
    );

    const querySnapshot = await getDocs(evaluationEventsQuery);

    // Map the querySnapshot to Event data
    const events: EventDTO[] = querySnapshot.docs.map(doc => {
      return doc.data() as EventDTO;
    });

    // Filter out events where user has already responded to evaluation
    const eventsToEvaluate = events.filter(event => {
      if (!event.evaluation) return false;

      // Convert evaluation to string format for hasUserRespondedEventEvaluation check
      const evaluationForCheck = {
        ...event.evaluation,
        endDate: event.evaluation.endDate.toDate().toISOString(),
      };

      return !hasUserRespondedEventEvaluation(evaluationForCheck, userId);
    });

    return {
      events: eventsToEvaluate,
    };
  } catch (error) {
    console.error('Error fetching events for evaluation:', error);
    throw new Error('Error fetching events for evaluation');
  }
};

const addEvaluationResponse = async (
  companyId: string,
  eventId: string,
  responses: QuestionResponses,
) => {
  try {
    const eventRef = doc(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.Events,
      eventId,
    );

    return runTransaction(firestore, async transaction => {
      const eventDoc = await transaction.get(eventRef);
      if (!eventDoc.exists()) {
        throw new Error('Event not found');
      }

      const eventData = eventDoc.data() as EventDTO;
      if (!eventData.evaluation) {
        throw new Error('This event does not have an evaluation.');
      }

      const currentResponses = eventData.evaluation.responses || {};
      const updatedResponses = {...currentResponses};

      Object.entries(responses).forEach(([questionId, response]) => {
        if (updatedResponses[questionId]) {
          updatedResponses[questionId] = [
            ...updatedResponses[questionId],
            ...response,
          ];
        } else {
          updatedResponses[questionId] = response;
        }
      });

      transaction.update(eventRef, {
        'evaluation.responses': updatedResponses,
      });
    });
  } catch (error) {
    console.error('Transaction failed:', error);
    throw error;
  }
};

const updateEvaluationEndDate = async (
  companyId: string,
  eventId: string,
  newEndDate: Timestamp,
) => {
  try {
    const eventRef = doc(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.Events,
      eventId,
    );

    await runTransaction(firestore, async transaction => {
      const eventDoc = await transaction.get(eventRef);
      if (!eventDoc.exists()) {
        throw new Error('Event not found');
      }

      const eventData = eventDoc.data() as EventDTO;
      if (!eventData.evaluation) {
        throw new Error('Event does not have an evaluation');
      }

      transaction.update(eventRef, {
        'evaluation.endDate': newEndDate,
      });
    });
  } catch (error) {
    console.error('Failed to update evaluation end date:', error);
    throw error;
  }
};

export type UpdateEventDetailsProps = {
  eventTitle?: FieldTranslation;
  eventDescription?: FieldTranslation;
  eventEmoji?: string;
  eventLocation?: string;
  eventTags?: string[];
  startDateTime?: Timestamp;
  endDateTime?: Timestamp | null;
  deadline?: Timestamp | null;
};
const updateEventDetails = async (
  companyId: string,
  eventId: string,
  updates: UpdateEventDetailsProps,
) => {
  try {
    const eventRef = doc(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.Events,
      eventId,
    );

    await runTransaction(firestore, async transaction => {
      const eventDoc = await transaction.get(eventRef);
      if (!eventDoc.exists()) {
        throw new Error('Event not found');
      }

      transaction.update(eventRef, updates);
    });
  } catch (error) {
    console.error('Failed to update event details:', error);
    throw error;
  }
};

interface SendIntegrationNotificationResponse {
  success: boolean;
  message: string;
}

const sendManualIntegrationNotification = async (
  companyId: string,
  eventId: string,
): Promise<SendIntegrationNotificationResponse> => {
  const callableFunc = httpsCallable<
    {
      companyId: string;
      eventId: string;
    },
    SendIntegrationNotificationResponse
  >(firebaseFunctions, 'onSendManualIntegrationNotification');

  return callableFunc({
    companyId,
    eventId,
  })
    .then(result => {
      console.log('Manual integration notification sent:', result.data);
      return result.data;
    })
    .catch(error => {
      console.error('Error sending manual integration notification:', error);
      throw error;
    });
};

// Backward compatibility alias
const sendManualSlackNotification = sendManualIntegrationNotification;

const deleteEvent = async (companyId: string, eventId: string) => {
  try {
    const eventRef = doc(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.Events,
      eventId,
    );

    await deleteDoc(eventRef);
  } catch (error) {
    console.error('Error deleting event:', error);
    throw new Error('Error deleting event');
  }
};

const getEventsCount = async (companyId: string): Promise<number> => {
  try {
    const companyRef = collection(
      firestore,
      FirestoreCollection.Companies,
      companyId,
      FirestoreCollection.Events,
    );

    const countSnapshot = await getCountFromServer(companyRef);
    return countSnapshot.data().count;
  } catch (error) {
    console.error('Error getting events count:', error);
    throw new Error('Error getting events count');
  }
};

export const event = {
  addEvent,
  getEvents,
  getEventById,
  getFutureEvents,
  getPastEvents,
  getEventsForEvaluation,
  getEventsCount,
  joinEvent,
  leaveEvent,
  acceptEventWithQuestionResponses,
  declineEvent,
  undeclineEvent,
  addEvaluationResponse,
  updateEvaluationEndDate,
  updateEventDetails,
  deleteEvent,
  sendManualSlackNotification, // Backward compatibility
  sendManualIntegrationNotification,
};
